/**
 * Converts duration string (e.g. "5", "09", "1:30", "123:45", "999:99") into total seconds
 * @param value - A string representing a duration in the format "HH:MM" or "MM".
 * @returns - The total duration in seconds.
 */
export function durationToSeconds(value: string): number {
  const parts = value.split(':');

  if (parts.length === 1) {
    const minutes = parseInt(parts[0], 10);
    return Number.isNaN(minutes) ? 0 : minutes * 60;
  }

  if (parts.length === 2) {
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);

    if (Number.isNaN(hours) || Number.isNaN(minutes) || hours < 0 || hours > 999 || minutes < 0 || minutes > 99) {
      return 0;
    }

    return hours * 3600 + minutes * 60;
  }

  return 0;
}

/**
 * Converts total seconds into duration string (e.g. "5", "09", "1:30", "123:45", "999:99")
 * @param seconds - A number representing total seconds.
 * @returns - A string representing the duration in this format: m, mm, h:mm, hh:mm, hhh:mm.
 */
export function secondsToDuration(seconds: number | undefined): string {
  if (!seconds || !Number.isFinite(seconds) || seconds < 0) return '0';

  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 999) return '999:99'; // clamp for safety
  if (minutes > 99) return `${hours}:99`; // invalid minutes

  if (hours === 0) {
    return totalMinutes < 10 ? `${totalMinutes}` : totalMinutes.toString().padStart(2, '0');
  }

  return `${hours}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Checks if the duration string is invalid (minutes > 59 or both hours and minutes are 0)
 * @param value - A string in the format "H:MM"
 * @returns - True if invalid, false otherwise.
 */
export function hasInvalidTime(value: string): boolean {
  const parts = value.split(':');

  if (parts.length !== 2) return true;

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) return true;

  return minutes > 59 || (minutes < 1 && hours < 1);
}
